// components/Reports/HiddenPdfTemplate.js
import React from "react";
import { basicReportColumn, componentColumn } from "./ConstantReports";
import { useRfi } from "../../contexts/RfiContext";

const HiddenPdfTemplate = () => {
  const { stagesData, rfiDetails } = useRfi();

  const { qc_inspector, qc_manager, qc_coordinator } = rfiDetails;
  const componentData = stagesData.dimensional.components;

  // Early return if no data
  if (!componentData || componentData.length === 0) {
    console.warn("HiddenPdfTemplate - No component data, returning null");
    return null;
  }

  const getBasicReportData = [
    {
      inspector_name:
        qc_inspector?.first_name + " " + qc_inspector?.last_name ||
        "QC Inspector",
      from: "Austin Engg.",
      date: "2025-10-14",
      issued_revised_by: "Austin QA/QC",
      reviewed_by:
        qc_coordinator?.first_name + " " + qc_coordinator?.last_name ||
        "QC Coordinator",
      approved_by:
        qc_manager?.first_name + " " + qc_manager?.last_name || "QC Manager",
    },
  ];
  return (
    <div
      // id="stage-1-report-template"
      style={{
        minHeight: "297mm",
        background: "#ffffff",
        boxSizing: "border-box",
        fontSize: "10px",
        lineHeight: "1.2",
        pointerEvents: "none",
        padding: 0,
        // zIndex: -1000000,
        // visibility: "hidden",
        // opacity: 0,
        // position: "fixed", // Modal uses relative, hidden uses fixed
        // left: "auto",
        // top: "auto",
        // width: "210mm",
      }}
    >
      {/* Basic Report Table Section */}
      <div
        data-pdf-section="basic-report"
        style={{ paddingBottom: "15px", overflow: "hidden" }}
      >
        <BasicReportTablePlain
          data={getBasicReportData}
          columns={basicReportColumn}
        />
      </div>

      {/* Components Section */}
      <ComponentsContainerPlain components={componentData} />
    </div>
  );
};

// Plain HTML version of BasicReportTable
const BasicReportTablePlain = ({ data, columns }) => {
  return (
    <div
      style={{
        // border: "1px solid #073642",
        marginBottom: "20px",
      }}
    >
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          fontSize: "9px",
        }}
      >
        <thead>
          <tr
            style={{
              backgroundColor: "#f8f9fa",
              border: "1px solid #cbd5e0",
              borderBottom: "none",
            }}
          >
            {columns?.map((column) => (
              <th
                key={column.id}
                style={{
                  padding: "6px 4px",
                  textAlign: "center",
                  color: "black",
                  border: "1px solid #cbd5e0",
                  fontWeight: "bold",
                  fontSize: "9px",
                }}
              >
                <div style={{ fontWeight: "bold" }}>{column.label}</div>
                {column.subLabel && (
                  <div
                    style={{
                      fontSize: "8px",
                      opacity: 0.9,
                      fontWeight: "normal",
                      marginTop: "1px",
                    }}
                  >
                    {column.subLabel}
                  </div>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data?.map((row, index) => (
            <tr
              key={index}
              style={{
                backgroundColor: index % 2 === 0 ? "#f8f9fa" : "white",
              }}
            >
              {columns.map((column) => (
                <td
                  key={column.id}
                  style={{
                    padding: "6px 4px",
                    border: "1px solid #cbd5e0",
                    textAlign: "center",
                    fontSize: "9px",
                  }}
                >
                  {row[column.id] || "—"}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Plain HTML version of ComponentsContainer
const ComponentsContainerPlain = ({ components }) => {
  if (!components || components.length === 0) {
    return (
      <div
        style={{
          textAlign: "center",
          // padding: "6px 10px",
          color: "#666",
          fontSize: "10px",
        }}
      >
        No components data available
      </div>
    );
  }

  return (
    <>
      {components?.map((component, compIndex) => (
        <div
          key={component.component_id || compIndex}
          style={{
            // marginBottom: "50px",
            pageBreakInside: "avoid",
            // padding: "6px 0px",
            paddingBottom: "30px",
            overflow: "hidden",
          }}
          // data-pdf-section={`components-${compIndex}`}
        >
          {/* Images */}
          {component.component_images &&
            component.component_images.length > 0 && (
              <div style={{ marginBottom: "15px" }}>
                <ImageContainerPlain
                  images={component.component_images}
                  component_name={component.component_name}
                />
              </div>
            )}

          {/* Dimensions Table */}
          {component.items && component.items.length > 0 && (
            <ComponentDimensionPlain
              items={component.items}
              columns={componentColumn}
            />
          )}
        </div>
      ))}
    </>
  );
};

// Plain HTML version of ImageContainer
const ImageContainerPlain = ({ images, component_name }) => {
  if (!images || images.length === 0) {
    return (
      <div
        style={{
          textAlign: "center",
          padding: "8px",
          color: "#666",
          fontStyle: "italic",
          fontSize: "10px",
        }}
      >
        No images available
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "6px",
      }}
    >
      {images?.reduce((acc, image, index) => {
        // Create a new row every 4 images
        if (index % 4 === 0) {
          const isFirstRow = index === 0;

          acc.push(
            <div
              key={`image-row-${index}`}
              data-pdf-section={`images-section-${index}`}
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "12px",
                width: "100%",
                paddingTop: "25px",
              }}
            >
              {/* Add title only for first row */}
              {isFirstRow && (
                <div
                  style={{
                    textAlign: "left",
                    backgroundColor: "#033047",
                    color: "white",
                    padding: "8px",
                    fontSize: "14px",
                    fontWeight: "bold",
                    marginBottom: "0px", // Reduced since we're using gap
                  }}
                >
                  Images and Dimensions of {component_name}
                </div>
              )}

              {/* Image row */}
              <div
                style={{
                  display: "flex",
                  gap: "12px",
                  width: "100%",
                  justifyContent: "left",
                }}
              >
                {images.slice(index, index + 4).map((img, innerIndex) => (
                  <img
                    key={img.image_id || index + innerIndex}
                    src={img.image_url}
                    alt={
                      img.image_id
                        ? `Component image ${img.image_id}`
                        : "Component image"
                    }
                    style={{
                      flex: "1",
                      minWidth: "0",
                      maxWidth: "calc(25% - 9px)", // 25% minus gap compensation
                      height: "120px",
                      objectFit: "contain",
                      backgroundColor: "#f5f5f5",
                      border: "1px solid #cbd5e0",
                      borderRadius: "5px",
                    }}
                    onError={(e) => {
                      console.error(`Failed to load image: ${img.image_url}`);
                      e.target.style.display = "none";
                    }}
                    onLoad={(e) => {
                      e.target.setAttribute("data-loaded", "true");
                    }}
                  />
                ))}
              </div>
            </div>
          );
        }
        return acc;
      }, [])}
    </div>
  );
};

// Plain HTML version of ComponentDimension - Fixed vertical align issue
const ComponentDimensionPlain = ({ items, columns }) => {
  if (!items || items.length === 0) {
    return (
      <div
        style={{
          textAlign: "center",
          padding: "15px",
          color: "#666",
          fontSize: "10px",
        }}
      >
        No dimension data available
      </div>
    );
  }

  return (
    <div
      style={{
        marginBottom: "8px",
        overflow: "hidden",
        paddingBottom: "20px",
      }}
      data-pdf-section="component-dimension-table"
    >
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          borderSpacing: 0,
          fontSize: "9px",
          border: "1px solid #cbd5e0",
          borderLeft: "2px solid #cbd5e0",
        }}
      >
        <thead>
          <tr style={{ backgroundColor: "#f8f9fa" }}>
            {columns?.map((column, index) => (
              <th
                key={column.id}
                style={{
                  padding: "6px 4px",
                  textAlign: "center",
                  color: "black",
                  border: "1px solid #cbd5e0",
                  borderLeft: index === 0 ? "1px solid #cbd5e0" : "none",
                  borderRight: "1px solid #cbd5e0",
                  fontWeight: "bold",
                  fontSize: "9px",
                }}
              >
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items?.map((group, groupIdx) => {
            const validSubparts = group?.sub_parts || [];

            if (validSubparts.length === 0) {
              return (
                <tr
                  key={`${groupIdx}-empty`}
                  style={{
                    // backgroundColor: groupIdx % 2 === 0 ? "#f8f9fa" : "white",
                    borderBottom: "1px solid #cbd5e0",
                  }}
                >
                  <td
                    style={{
                      padding: "3px 2px",
                      borderRight: "1px solid #cbd5e0",
                      textAlign: "center",
                      fontSize: "9px",
                      borderBottom: "1px solid #cbd5e0",
                    }}
                  >
                    {group.item_name}
                  </td>
                  {columns.slice(1).map((column, colIdx) => (
                    <td
                      key={colIdx}
                      style={{
                        padding: "3px 2px",
                        borderRight: "1px solid #cbd5e0",
                        textAlign: "center",
                        fontSize: "9px",
                      }}
                    >
                      —
                    </td>
                  ))}
                </tr>
              );
            }

            return validSubparts.map((subpart, subpartIdx) => (
              <tr
                key={`${groupIdx}-${subpartIdx}`}
                style={{
                  backgroundColor: groupIdx % 2 === 0 ? "#f8f9fa" : "white",
                }}
              >
                {/* Item Name - only show for first subpart with rowspan */}
                {subpartIdx === 0 ? (
                  <td
                    rowSpan={validSubparts.length}
                    style={{
                      padding: "3px 2px",
                      borderRight: "1px solid #cbd5e0",
                      borderBottom: "1px solid #cbd5e0",
                      textAlign: "center",
                      fontWeight: "bold",
                      verticalAlign: "top", // Use 'top' instead of 'middle' for PDF compatibility
                      fontSize: "9px",
                      height: "100%",
                      position: "relative",
                    }}
                  >
                    {/* Wrap in a div for better vertical centering control */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minHeight: `${validSubparts.length * 30}px`, // Adjust based on your row height
                      }}
                    >
                      {group.item_name}
                    </div>
                  </td>
                ) : null}

                {/* Description */}
                <td
                  style={{
                    padding: "3px 2px",
                    borderRight: "1px solid #cbd5e0",
                    borderBottom: "1px solid #cbd5e0",
                    textAlign: "center",
                    fontSize: "9px",
                    verticalAlign: "middle",
                  }}
                >
                  {subpart?.description?.trim() || "—"}
                </td>

                {/* Specification */}
                <td
                  style={{
                    padding: "3px 2px",
                    borderRight: "1px solid #cbd5e0",
                    borderBottom: "1px solid #cbd5e0",
                    textAlign: "center",
                    fontSize: "9px",
                    verticalAlign: "middle",
                  }}
                >
                  {subpart?.specification?.trim() || "—"}
                </td>

                {/* Tolerance */}
                <td
                  style={{
                    padding: "3px 2px",
                    borderRight: "1px solid #cbd5e0",
                    borderBottom: "1px solid #cbd5e0",
                    textAlign: "center",
                    fontSize: "9px",
                    verticalAlign: "middle",
                  }}
                >
                  {subpart?.tolerance || "—"}
                </td>

                {/* Actual Inspection */}
                <td
                  style={{
                    padding: "3px 2px",
                    borderRight: "1px solid #cbd5e0",
                    borderBottom: "1px solid #cbd5e0",
                    textAlign: "center",
                    fontSize: "9px",
                    verticalAlign: "middle",
                  }}
                >
                  {subpart?.actual_inspection || "—"}
                </td>

                {/* Finish Process */}
                <td
                  style={{
                    padding: "3px 2px",
                    borderRight: "1px solid #cbd5e0",
                    borderBottom: "1px solid #cbd5e0",
                    textAlign: "center",
                    fontSize: "9px",
                    verticalAlign: "middle",
                  }}
                >
                  {subpart?.finish_process ? "Yes" : "No"}
                </td>

                {/* Remark */}
                <td
                  style={{
                    padding: "3px 2px",
                    borderRight: "1px solid #cbd5e0",
                    borderBottom: "1px solid #cbd5e0",
                    textAlign: "center",
                    fontSize: "9px",
                    wordBreak: "break-word",
                    maxWidth: "120px",
                    verticalAlign: "middle",
                  }}
                >
                  {subpart?.remark || "—"}
                </td>
              </tr>
            ));
          })}
        </tbody>
      </table>
    </div>
  );
};

export default HiddenPdfTemplate;
